import React from "react";
import { Box, Divider, IconButton, LinearProgress, Tooltip, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MovieFilterIcon from "@mui/icons-material/MovieFilter";

const INDIGO = "#312e81";

/**
 * The batch editor's one header row: what is open on the left, the template
 * and platform tabs (`tabs`, a PlatformTabBar) in the middle, then overall
 * progress and close.
 */
export default function BatchEditorHeader({ activeItem, multiBrand, savedCount, appliedCount = 0, categoryMissingCount = 0, total, onClose, tabs }) {
  const title = activeItem && (activeItem.video.title || activeItem.video.video_title);
  const subtitle = activeItem && `${multiBrand ? `${activeItem.brandName} · ` : ""}${activeItem.wgid}${title ? ` · ${title}` : ""}`;
  return (
    <Box
      sx={{
        px: 1.25, minHeight: 34, display: "flex", alignItems: "center", gap: 0.75, bgcolor: "white", borderBottom: "1px solid #e2e8f0", flexShrink: 0,
        // The row wraps whenever it runs out of room (the tab's controls,
        // progress, source and close drop to a second line) instead of
        // squeezing the platform tabs away — at any width, since a YouTube
        // tab's template switch alone can fill a wide screen (2026-10-09).
        flexWrap: "wrap",
        rowGap: 0.5,
        py: 0.25,
      }}
    >
      <Box display="flex" alignItems="center" gap={1} minWidth={0} flexShrink={1} sx={{ maxWidth: { xs: 160, lg: 260 } }}>
        <MovieFilterIcon sx={{ fontSize: 14, color: "#4338ca", flexShrink: 0 }} />
        <Box minWidth={0}>
          <Typography id="batch-editor-title" variant="subtitle2" fontWeight={700} noWrap sx={{ color: INDIGO, lineHeight: 1.1, fontSize: 12 }}>
            Batch Editor
          </Typography>
          {subtitle && (
            <Typography variant="caption" noWrap component="div" title={subtitle} sx={{ color: "text.secondary", lineHeight: 1.15, fontSize: 9.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      <Divider orientation="vertical" flexItem sx={{ my: 0.75, borderColor: "#e2e8f0" }} />

      {tabs}

      <Box display="flex" alignItems="center" gap={1} flexShrink={0} sx={{ ml: "auto" }}>
        <Typography role="status" aria-atomic="true" variant="caption" fontWeight={700} noWrap sx={{ color: "#334155", fontSize: 10.5 }}>
          Edited {savedCount} / {total}
          {appliedCount > 0 && (
            <Box component="span" sx={{ ml: 1, color: "#2563eb" }}>
              · {appliedCount} to review
            </Box>
          )}
          {categoryMissingCount > 0 && (
            <Box component="span" sx={{ ml: 1, color: "#b91c1c" }}>
              · {categoryMissingCount} need a category
            </Box>
          )}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={total ? (savedCount / total) * 100 : 0}
          aria-hidden
          sx={{ width: 52, height: 4, borderRadius: 3, bgcolor: "#e2e8f0", "& .MuiLinearProgress-bar": { bgcolor: "#22c55e", borderRadius: 3 } }}
        />
        <Tooltip title="Close">
          <IconButton aria-label="Close batch editor" onClick={onClose} size="small" sx={{ color: "#64748b", "&:hover": { color: "#0f172a", bgcolor: "#f1f5f9" } }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
